import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from './I18nContext';

/* ============================================================
   Testimonials — Dark Editorial Interview
   Aesthetic: OLED Sovereign × Luxury Magazine
   One quote owns the frame. Auto-advance. Typographic anchor.
============================================================ */

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  location: string;
  flag: string;
  sector: string;
  index: string;
}

const TESTIMONIALS_DATA = {
  de: [
    {
      quote: 'Wir hatten jahrelang Bedenken, unsere Produktionsdaten in die Cloud zu geben. HenselX hat uns gezeigt, dass Bare-Metal-Performance und EU-Compliance gleichzeitig möglich sind. Seit der Migration: null Ausfälle. Zero.',
      name: 'Klaus Brückmann', role: 'Geschäftsführer', company: 'Brückmann Maschinenbau GmbH',
      location: 'Düsseldorf, Deutschland', flag: '🇩🇪', sector: 'Server Infrastructure', index: '01',
    },
    {
      quote: 'Patientendaten dürfen nie eine US-Cloud berühren. HenselX hat eine vollständig airgegappte KI-Infrastruktur geliefert — schnell, präzise, vollständig unter unserer Kontrolle. Das ist keine IT-Dienstleistung. Das ist Partnerschaft.',
      name: 'Valentina Greco', role: 'CTO', company: 'MedCore Srl',
      location: 'Milano, Italia', flag: '🇮🇹', sector: 'AI · Healthcare', index: '02',
    },
    {
      quote: 'Wir haben drei Agenturen getestet. HenselX hat als einzige die Komplexität unserer B2B-Plattform wirklich durchdrungen — und pünktlich geliefert. Der Code ist chirurgisch sauber. Kein technischer Debt, keine Ausreden.',
      name: 'Stefan Kovář', role: 'Founder & CEO', company: 'Orbio Systems s.r.o.',
      location: 'Praha, Česko', flag: '🇨🇿', sector: 'App Development', index: '03',
    },
    {
      quote: 'Endlich eine Agentur, die DSGVO nicht als Ausrede für schlechtes Marketing benutzt. Die Kampagnen performen — ohne Tracking-Pixel, ohne US-Dienste. Unsere Juristen lieben es. Unsere Conversion-Rate auch.',
      name: 'Irene Metzger', role: 'Marketing Director', company: 'AlpenKraft GmbH',
      location: 'Wien, Österreich', flag: '🇦🇹', sector: 'Social Media · DSGVO', index: '04',
    },
    {
      quote: 'HenselX ist der einzige Partner, der Kalabrien wirklich versteht — die Region, die Mentalität, die Netzwerke. Die App hat unsere Direktvermarktung revolutioniert. Ernte-zu-Endkunde. Eine Plattform. Keine Kompromisse.',
      name: 'Francesco Battaglia', role: 'CEO', company: 'Battaglia Agritech Srl',
      location: 'Cosenza, Italia', flag: '🇮🇹', sector: 'App Development', index: '05',
    },
    {
      quote: "In unserer Branche sind 200ms Latenz ein Wettbewerbsnachteil. Mit HenselX's Bare-Metal-Setup liegen wir intern bei 12ms. Die KI-Routenoptimierung spart uns täglich fünfstellige Beträge. ROI in 6 Wochen.",
      name: 'Dominik Hartmann', role: 'Head of IT', company: 'Hartmann Logistik SE',
      location: 'Hamburg, Deutschland', flag: '🇩🇪', sector: 'AI · Infrastructure', index: '06',
    },
  ],
  it: [
    {
      quote: 'Per anni abbiamo esitato a mettere i nostri dati di produzione nel cloud. HenselX ci ha dimostrato che performance Bare-Metal e conformità UE possono coesistere. Dalla migrazione: zero interruzioni. Zero.',
      name: 'Klaus Brückmann', role: 'Geschäftsführer', company: 'Brückmann Maschinenbau GmbH',
      location: 'Düsseldorf, Germania', flag: '🇩🇪', sector: 'Server Infrastructure', index: '01',
    },
    {
      quote: "I dati dei pazienti non devono mai toccare un cloud statunitense. HenselX ha consegnato un'infrastruttura AI completamente air-gapped — veloce, precisa, completamente sotto il nostro controllo. Non è un servizio IT. È una partnership.",
      name: 'Valentina Greco', role: 'CTO', company: 'MedCore Srl',
      location: 'Milano, Italia', flag: '🇮🇹', sector: 'AI · Healthcare', index: '02',
    },
    {
      quote: 'Abbiamo testato tre agenzie. HenselX è stata l\'unica a comprendere davvero la complessità della nostra piattaforma B2B — e a consegnarla puntualmente. Il codice è chirurgicamente pulito. Zero debito tecnico, zero scuse.',
      name: 'Stefan Kovář', role: 'Founder & CEO', company: 'Orbio Systems s.r.o.',
      location: 'Praha, Česko', flag: '🇨🇿', sector: 'App Development', index: '03',
    },
    {
      quote: 'Finalmente un\'agenzia che non usa il GDPR come scusa per un marketing scadente. Le campagne performano — senza pixel di tracciamento, senza servizi statunitensi. I nostri legali adorano. Anche il nostro tasso di conversione.',
      name: 'Irene Metzger', role: 'Marketing Director', company: 'AlpenKraft GmbH',
      location: 'Wien, Austria', flag: '🇦🇹', sector: 'Social Media · GDPR', index: '04',
    },
    {
      quote: 'HenselX è l\'unico partner che capisce davvero la Calabria — la regione, la mentalità, le reti. L\'app ha rivoluzionato la nostra vendita diretta. Dal raccolto al consumatore finale. Una piattaforma. Nessun compromesso.',
      name: 'Francesco Battaglia', role: 'CEO', company: 'Battaglia Agritech Srl',
      location: 'Cosenza, Italia', flag: '🇮🇹', sector: 'App Development', index: '05',
    },
    {
      quote: "Nel nostro settore, 200ms di latenza sono uno svantaggio competitivo. Con il setup Bare-Metal di HenselX siamo a 12ms interni. L'ottimizzazione AI dei percorsi ci fa risparmiare cifre a cinque zeri ogni giorno. ROI in 6 settimane.",
      name: 'Dominik Hartmann', role: 'Head of IT', company: 'Hartmann Logistik SE',
      location: 'Hamburg, Germania', flag: '🇩🇪', sector: 'AI · Infrastructure', index: '06',
    },
  ],
  en: [
    {
      quote: 'For years we were reluctant to put our production data in the cloud. HenselX showed us that Bare-Metal performance and EU compliance can coexist. Since the migration: zero outages. Zero.',
      name: 'Klaus Brückmann', role: 'Geschäftsführer', company: 'Brückmann Maschinenbau GmbH',
      location: 'Düsseldorf, Germany', flag: '🇩🇪', sector: 'Server Infrastructure', index: '01',
    },
    {
      quote: 'Patient data must never touch a US cloud. HenselX delivered a fully air-gapped AI infrastructure — fast, precise, completely under our control. This is not an IT service. This is partnership.',
      name: 'Valentina Greco', role: 'CTO', company: 'MedCore Srl',
      location: 'Milano, Italy', flag: '🇮🇹', sector: 'AI · Healthcare', index: '02',
    },
    {
      quote: 'We tested three agencies. HenselX was the only one that truly grasped the complexity of our B2B platform — and delivered on time. The code is surgically clean. No technical debt, no excuses.',
      name: 'Stefan Kovář', role: 'Founder & CEO', company: 'Orbio Systems s.r.o.',
      location: 'Praha, Czech Republic', flag: '🇨🇿', sector: 'App Development', index: '03',
    },
    {
      quote: 'Finally an agency that does not use GDPR as an excuse for poor marketing. The campaigns perform — without tracking pixels, without US services. Our lawyers love it. So does our conversion rate.',
      name: 'Irene Metzger', role: 'Marketing Director', company: 'AlpenKraft GmbH',
      location: 'Wien, Austria', flag: '🇦🇹', sector: 'Social Media · GDPR', index: '04',
    },
    {
      quote: 'HenselX is the only partner that truly understands Calabria — the region, the mindset, the networks. The app revolutionised our direct marketing. Harvest to end customer. One platform. No compromises.',
      name: 'Francesco Battaglia', role: 'CEO', company: 'Battaglia Agritech Srl',
      location: 'Cosenza, Italy', flag: '🇮🇹', sector: 'App Development', index: '05',
    },
    {
      quote: "In our industry, 200ms latency is a competitive disadvantage. With HenselX's Bare-Metal setup we are at 12ms internally. The AI route optimisation saves us five-figure sums every day. ROI in 6 weeks.",
      name: 'Dominik Hartmann', role: 'Head of IT', company: 'Hartmann Logistik SE',
      location: 'Hamburg, Germany', flag: '🇩🇪', sector: 'AI · Infrastructure', index: '06',
    },
  ],
};

const INTERVAL = 7000;

/* Word-by-word reveal animation */
const QuoteText: React.FC<{ text: string }> = ({ text }) => {
  const words = text.split(' ');
  return (
    <span>
      {words.map((word, i) => (
        <motion.span
          key={`${word}-${i}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.35,
            delay: i * 0.018,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="inline-block mr-[0.28em]"
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
};

export const Testimonials: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  const { t, language } = useTranslation();
  const TESTIMONIALS = TESTIMONIALS_DATA[language as keyof typeof TESTIMONIALS_DATA] ?? TESTIMONIALS_DATA.de;

  const total = TESTIMONIALS.length;
  const active = TESTIMONIALS[activeIndex];

  const goTo = useCallback(
    (index: number, dir: 1 | -1 = 1) => {
      setDirection(dir);
      setActiveIndex((index + total) % total);
      setProgress(0);
    },
    [total],
  );

  const next = useCallback(() => goTo(activeIndex + 1, 1), [activeIndex, goTo]);
  const prev = useCallback(() => goTo(activeIndex - 1, -1), [activeIndex, goTo]);

  /* Auto-advance */
  useEffect(() => {
    if (isPaused) return;
    const step = 50;
    const increment = (step / INTERVAL) * 100;
    const timer = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          next();
          return 0;
        }
        return p + increment;
      });
    }, step);
    return () => clearInterval(timer);
  }, [isPaused, next]);

  const slideVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir * 40, filter: 'blur(4px)' }),
    center: { opacity: 1, x: 0, filter: 'blur(0px)' },
    exit: (dir: number) => ({ opacity: 0, x: dir * -30, filter: 'blur(4px)' }),
  };

  return (
    <section
      className="relative bg-white dark:bg-[#000000] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="hx-section-line" />

      {/* Auto-advance progress bar */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-slate-100 dark:bg-white/5 z-30">
        <motion.div
          className="h-full bg-[#0ea5e9]"
          style={{ width: `${progress}%` }}
          transition={{ duration: 0 }}
        />
      </div>

      {/* Ghost quotation mark — the memorable typographic anchor */}
      <div
        className="absolute left-6 md:left-16 top-8 select-none pointer-events-none z-0 leading-none font-serif"
        style={{
          fontSize: 'clamp(160px, 22vw, 320px)',
          color: 'transparent',
          WebkitTextStroke: '1px rgba(14,165,233,0.07)',
          lineHeight: 1,
        }}
        aria-hidden
      >
        "
      </div>

      <div className="absolute inset-0 hx-grid-bg pointer-events-none opacity-50" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">

        {/* Section header row */}
        <div className="flex items-center justify-between mb-16 md:mb-20">
          <div>
            <span className="hx-badge mb-4 inline-block">{t('testimonials.badge')}</span>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-none">
              {t('testimonials.title')}<br />
              <span className="text-[#0ea5e9]">{t('testimonials.titleHighlight')}</span>
            </h2>
          </div>

          {/* Counter + Nav — top-right */}
          <div className="hidden md:flex flex-col items-end gap-4">
            <motion.span
              key={activeIndex}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-mono text-sm text-slate-400 dark:text-slate-600 tabular-nums"
            >
              <span className="text-slate-900 dark:text-white font-bold">{active.index}</span>
              {' '}/ {String(total).padStart(2, '0')}
            </motion.span>
            <div className="flex gap-2">
              <button
                onClick={prev}
                aria-label="Vorheriges Testimonial"
                className="w-9 h-9 rounded-sm border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500 dark:text-slate-500 hover:border-[#0ea5e9]/50 hover:text-[#0ea5e9] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                aria-label="Nächstes Testimonial"
                className="w-9 h-9 rounded-sm border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500 dark:text-slate-500 hover:border-[#0ea5e9]/50 hover:text-[#0ea5e9] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Main quote area */}
        <div className="min-h-[220px] md:min-h-[180px] mb-14 relative">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.blockquote
              key={activeIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0"
            >
              <p className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white leading-tight tracking-tight max-w-4xl">
                <QuoteText text={`"${active.quote}"`} />
              </p>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        {/* Attribution + dots row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">

          {/* Client attribution */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`attr-${activeIndex}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-start gap-5"
            >
              {/* Monogram avatar */}
              <div className="w-12 h-12 rounded-sm bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 flex items-center justify-center shrink-0">
                <span className="font-mono text-[#0ea5e9] text-base font-bold leading-none">
                  {active.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>

              <div>
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-slate-900 dark:text-white font-bold text-base">{active.name}</span>
                  <span className="text-base leading-none">{active.flag}</span>
                </div>
                <p className="font-mono text-xs text-slate-500 dark:text-slate-500 mb-1">
                  {active.role} · {active.company}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-slate-400 dark:text-slate-600 tracking-wide">
                    {active.location}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-white/15 inline-block" />
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#0ea5e9]/20 text-[#0ea5e9]/70">
                    {active.sector}
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dot navigation */}
          <div className="flex items-center gap-2.5">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > activeIndex ? 1 : -1)}
                aria-label={`Testimonial ${i + 1}`}
                className="group relative flex items-center justify-center"
              >
                <motion.div
                  animate={{
                    width: i === activeIndex ? 24 : 6,
                    backgroundColor:
                      i === activeIndex ? '#0ea5e9' : undefined,
                  }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="h-1.5 rounded-full bg-slate-300 dark:bg-white/15 group-hover:bg-slate-400 dark:group-hover:bg-white/30 transition-colors"
                  style={{ width: i === activeIndex ? 24 : 6 }}
                />
              </button>
            ))}

            {/* Mobile nav arrows */}
            <div className="flex gap-1.5 ml-3 md:hidden">
              <button
                onClick={prev}
                className="w-8 h-8 rounded-sm border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                className="w-8 h-8 rounded-sm border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Thin separator stripe with all client companies */}
        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-white/5 overflow-hidden">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {TESTIMONIALS.map((testimonial, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > activeIndex ? 1 : -1)}
                className={`font-mono text-[11px] tracking-wide transition-colors duration-200 ${
                  i === activeIndex
                    ? 'text-slate-900 dark:text-white'
                    : 'text-slate-400 dark:text-slate-700 hover:text-slate-600 dark:hover:text-slate-500'
                }`}
              >
                {testimonial.company}
              </button>
            ))}
          </div>
        </div>

      </div>
      <div className="hx-section-line" />
    </section>
  );
};
